import { useDispatch, useSelector } from "react-redux";

import Dropdown from "@/components/ui/dropdown";
import type { IOptionItem } from "@/components/ui/dropdown/types";
import { useMap } from "@/hooks/map/core/useMapContext.ts";
import { useAppFeatures } from "@/hooks/useAppFeatures.tsx";
import { RootState } from "@/store";
import {
  clearPolygonSettingsTemp,
  setAdminAreaAddress,
  setAdminAreaSettingsData,
  setAdminTreeSettingsSelectedIds,
  setAreaInput,
  setOverrideGlobalFilter,
  setSpatialConfig
} from "@/store/slice/spatialSlice";
import {
  setShowPolygonPopup,
  toggleAllowPinMarker,
  toggleAreaAdminPopup,
  toggleCatchmentPopup
} from "@/store/slice/uiSlice";
import { AreaInputType } from "@/types/spatial";

import { area } from "@turf/turf";
import { Button } from "equator-design-system";

import AdminAreaSelected from "./adminAreaSelected";
import CatchmentSelected from "./catchmentSelected";
import PolygonSelected from "./polygonSelected.tsx";

type Props = {
  isError?: boolean;
  onOpenCatchmentPopup: (value: boolean) => void;
  onOpenAreaAdminPopup: (value: boolean) => void;
  onOpenPolygonPopup: (value: boolean) => void;
};

const SpatialAreaInput = ({
  isError = false,
  onOpenCatchmentPopup,
  onOpenAreaAdminPopup,
  onOpenPolygonPopup
}: Props) => {
  const dispatch = useDispatch();
  const { isDrawPolygonEnabled, isAdminTreeEnabled } = useAppFeatures();
  const { renderDrawPolygon, deleteAllFeatures } = useMap();

  const { showAreaAdminPopup } = useSelector((state: RootState) => state.ui);
  const { spatialConfig, overrideGlobalFilter } = useSelector(
    (state: RootState) => state.spatial
  );
  const { adminAreaSettings, adminAreaSettingsData, selectedAreaIds } =
    useSelector((state: RootState) => state.globalFilter);

  const handleSelectAreaInput = (data: IOptionItem | null) => {
    if (!data) return;

    dispatch(setAreaInput(data));

    const wasPolygon = spatialConfig.areaInput?.value === AreaInputType.POLYGON;
    const isPolygon = data.value === AreaInputType.POLYGON;

    if (wasPolygon && !isPolygon) {
      dispatch(setShowPolygonPopup(false));
      dispatch(clearPolygonSettingsTemp());
    }

    if (data.value === AreaInputType.ADMIN_AREA) {
      dispatch(toggleCatchmentPopup(false));
      dispatch(toggleAllowPinMarker(false));
      dispatch(setOverrideGlobalFilter(!adminAreaSettings));

      dispatch(setAdminAreaAddress(adminAreaSettings));
      dispatch(setAdminAreaSettingsData(adminAreaSettingsData));
      const areaSize =
        adminAreaSettingsData?.data.reduce(
          (acc, curr) => acc + area(curr.boundary),
          0
        ) ?? 0;
      dispatch(
        setSpatialConfig({
          ...spatialConfig,
          areaInput: data,
          areaSize,
          gridResolution: null,
          h3Resolution: null
        })
      );
      if (isAdminTreeEnabled) {
        dispatch(setAdminTreeSettingsSelectedIds(selectedAreaIds));
      }
    } else if (isPolygon) {
      renderDrawPolygon();
      dispatch(toggleCatchmentPopup(false));
      dispatch(toggleAreaAdminPopup(false));
    } else {
      dispatch(toggleAreaAdminPopup(false));
    }

    if (!isPolygon) {
      deleteAllFeatures();
    }
  };

  return (
    <div className="flex flex-col px-4 gap-1 font-sans">
      <span className="font-semibold text-txtHeading text-sm">Area Input</span>
      <p className="text-txtBody text-[10px]">
        Select a method: catchment or administrative area.
      </p>
      <div className="mt-2">
        <Dropdown
          className="text-sm"
          isValidationError={isError}
          value={spatialConfig.areaInput}
          placeholder="Define by"
          labelBorder="Define by"
          data={[
            { label: "Administrative Area", value: AreaInputType.ADMIN_AREA },
            { label: "Catchment", value: AreaInputType.CATCHMENT },
            ...(isDrawPolygonEnabled
              ? [{ label: "Polygon", value: AreaInputType.POLYGON }]
              : [])
          ]}
          onSelect={handleSelectAreaInput}
        />
      </div>
      {/* Administrative */}
      {spatialConfig.areaInput?.value === AreaInputType.ADMIN_AREA && (
        <div className="flex flex-col mt-2 gap-3">
          <AdminAreaSelected
            disableOverrideFilter={!adminAreaSettings || showAreaAdminPopup}
            overrideFilter={overrideGlobalFilter}
            onToggleOverrideFilter={(value) => {
              dispatch(setOverrideGlobalFilter(value));
              if (!value) {
                dispatch(setAdminAreaAddress(adminAreaSettings));
                dispatch(setAdminAreaSettingsData(adminAreaSettingsData));
                const areaSize =
                  adminAreaSettingsData?.data.reduce(
                    (acc, curr) => acc + area(curr.boundary),
                    0
                  ) ?? 0;
                dispatch(
                  setSpatialConfig({
                    ...spatialConfig,
                    areaSize,
                    gridResolution: null,
                    h3Resolution: null
                  })
                );
                if (isAdminTreeEnabled) {
                  dispatch(setAdminTreeSettingsSelectedIds(selectedAreaIds));
                }
              }
            }}
          />
          <Button
            variant="secondary"
            className="h-[32px] w-full border border-teal-500 bg-white text-teal-500 text-sm font-sans font-bold disabled:border-gainsboro disabled:border disabled:text-gainsboro"
            disabled={!overrideGlobalFilter}
            onClick={() => onOpenAreaAdminPopup(true)}
          >
            <span>Set Administrative Area</span>
          </Button>
        </div>
      )}
      {/* Catchment */}
      {spatialConfig.areaInput?.value === AreaInputType.CATCHMENT && (
        <div className="flex flex-col mt-2 gap-3">
          <CatchmentSelected />
          <Button
            className="h-[32px] w-full border border-teal-500 bg-white text-teal-500 text-sm font-sans font-bold"
            onClick={() => onOpenCatchmentPopup(true)}
          >
            <span>Set Catchment </span>
          </Button>
        </div>
      )}

      {isDrawPolygonEnabled &&
        spatialConfig.areaInput?.value === AreaInputType.POLYGON && (
          <div className="flex flex-col mt-2 gap-3">
            <PolygonSelected />
            <Button
              className="h-[32px] w-full border border-teal-500 bg-white text-teal-500 text-sm font-sans font-bold"
              onClick={() => onOpenPolygonPopup(true)}
            >
              <span>Set Polygon Area</span>
            </Button>
          </div>
        )}
    </div>
  );
};

export default SpatialAreaInput;
